import { getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  EQueryParamCategoryElementType,
  IQueryParam,
  IQueryParamCategory,
  IQueryParamGroup,
} from '~/src/dash/types/endpoint-query-params';
import { ALL, ANY } from '~/src/dash/utils/endpoint-builder-helpers';

const AdBreakParamCategories: IQueryParamCategory[] = [
  {
    title: 'Type',
    subTitle: 'Mandatory field',
    elementType: EQueryParamCategoryElementType.CHIPS,
    description: `Define if you will be calling the ${getResellerTitle()} endpoint from an app (Mobile or CTV) or a website.`,
    multiSelect: false,
    mandatory: true,
    queryParams: [
      { text: 'App', paramName: 'app_bundle', paramValue: '[APP_BUNDLE]' },
      { text: 'Website', paramName: 'site_page', paramValue: '[SITE_PAGE]' },
    ],
    display: true,
  },
  {
    title: 'Definition',
    subTitle: 'Mandatory field',
    elementType: EQueryParamCategoryElementType.CHIPS,
    description: `Please ask your ${getResellerTitle()} account manager or refer to our documentation if you aren't sure which integration type is right for you.`,
    multiSelect: false,
    mandatory: true,
    queryParams: [
      { text: 'VMAP', paramName: 'vmap', paramValue: '[VMAP_URL]' },
      { text: 'Query Params', paramName: 'ad_breaks', paramValue: '[TIME_OFFSET],[TIME_OFFSET]' },
      // add ' (invisible)' to param names that are added by default along with another params selection - in this case, pod_duration is added with Query Params
      // { text: '', paramName: 'pod_duration (invisible)', paramValue: '[POD_DURATION]' },
      { text: 'HLS Ad Markers', paramName: 'hls_marker', paramValue: '1' },
    ],
    display: true,
  },
  {
    title: 'Pod Construction',
    elementType: EQueryParamCategoryElementType.CHIPS,
    description:
      'Ad Pods can be defined either as a duration in seconds or by the number of slots available in the pod.',
    multiSelect: false,
    mandatory: false,
    queryParams: [
      { text: 'Pod Duration', paramName: 'pod_duration', paramValue: '[POD_DURATION]' },
      { text: 'Slot Count', paramName: 'slot_count', paramValue: '[SLOT_COUNT]' },
    ],
    display: true,
  },
  {
    title: 'Min & Max Ad Duration',
    subTitle: 'in seconds',
    elementType: EQueryParamCategoryElementType.SLIDER,
    description: '',
    multiSelect: false,
    mandatory: false,
    queryParams: [
      // query params for sliders contain a default value that is overwritten by slider values
      { text: 'Min Ad Duration', paramName: 'min_ad_duration', paramValue: '6' },
      { text: 'Max Ad Duration', paramName: 'max_ad_duration', paramValue: '120' },
    ],
    display: false,
  },
];

const AdditionalParamsCategories: IQueryParamCategory[] = [
  {
    title: 'App',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'App Name', paramName: 'app_name', paramValue: '[APP_NAME]' },
      { text: 'App Domain', paramName: 'app_domain', paramValue: '[APP_DOMAIN]' },
      { text: 'App Version', paramName: 'app_version', paramValue: '[APP_VERSION]' },
      { text: 'App Category', paramName: 'app_category', paramValue: '[APP_CATEGORY]' },
      { text: 'App Store URL', paramName: 'app_store_url', paramValue: '[APP_STORE_URL]' },
    ],
    display: false,
  },
  {
    title: 'Site',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [{ text: 'Site Name', paramName: 'site_name', paramValue: '[SITE_NAME]' }],
    display: false,
  },
  {
    title: 'Content',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'Content ID', paramName: 'content_id', paramValue: '[CONTENT_ID]' },
      { text: 'Content URL', paramName: 'content_url', paramValue: '[CONTENT_URL]' },
      { text: 'Content Category', paramName: 'content_cat', paramValue: '[CONTENT_CAT]' },
      { text: 'Content Title', paramName: 'content_title', paramValue: '[CONTENT_TITLE]' },
      { text: 'Content Genre', paramName: 'content_genre', paramValue: '[CONTENT_GENRE]' },
      { text: 'Content Rating', paramName: 'content_rating', paramValue: '[CONTENT_RATING]' },
      { text: 'Content Season', paramName: 'content_season', paramValue: '[CONTENT_SEASON]' },
      { text: 'Content Series', paramName: 'content_series', paramValue: '[CONTENT_SERIES]' },
      { text: 'Content Length', paramName: 'content_length', paramValue: '[CONTENT_LENGTH]' },
      { text: 'Content Channel', paramName: 'content_channel', paramValue: '[CONTENT_CHANNEL]' },
      { text: 'Content Context', paramName: 'content_context', paramValue: '[CONTENT_CONTEXT]' },
      { text: 'Content Episode', paramName: 'content_episode', paramValue: '[CONTENT_EPISODE]' },
      { text: 'Content Network', paramName: 'content_network', paramValue: '[CONTENT_NETWORK]' },
      { text: 'Content Keywords', paramName: 'content_keywords', paramValue: '[CONTENT_KEYWORDS]' },
      { text: 'Content Language', paramName: 'content_language', paramValue: '[CONTENT_LANGUAGE]' },
      {
        text: 'Content Prod. Quality',
        paramName: 'content_prodqual',
        paramValue: '[CONTENT_PROD]',
      },
      {
        text: 'Content Distribution ID',
        paramName: 'content_dist_id',
        paramValue: '[CONTENT_DIST_ID]',
      },
      {
        text: 'Content Producer',
        paramName: 'content_producer',
        paramValue: '[CONTENT_PRODUCER_NAME]',
      },
      {
        text: 'Content Producer ID',
        paramName: 'content_producer_id',
        paramValue: '[CONTENT_PRODUCER_ID]',
      },
      {
        text: 'Content Distribution Name',
        paramName: 'content_dist_name',
        paramValue: '[CONTENT_DIST_NAME]',
      },
    ],
    display: true,
  },
  {
    title: 'Device',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'Device Type', paramName: 'device_type', paramValue: '[DEVICE_TYPE]' },
      { text: 'Device Language', paramName: 'device_language', paramValue: '[DEVICE_LANGUAGE]' },
      { text: 'Device Latitude', paramName: 'lat', paramValue: '[LAT]' },
      { text: 'Device Longitude', paramName: 'lon', paramValue: '[LON]' },
      { text: 'IP', paramName: 'ip', paramValue: '[IP]' },
      { text: 'User Agent', paramName: 'ua', paramValue: '[%UA%]' },
    ],
    display: true,
  },
  {
    title: 'User',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'Gender', paramName: 'gender', paramValue: '[GENDER]' },
      { text: "User's Year Of Birth", paramName: 'yob', paramValue: '[YOB]' },
      { text: 'Session ID', paramName: 'session_id', paramValue: '[SESSION_ID]' },
      {
        text: 'Preferred Language',
        paramName: 'preferred_language',
        paramValue: '[PREFERRED_LANGUAGE]',
      },
    ],
    display: true,
  },
  {
    title: 'Privacy',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'GDPR', paramName: 'gdpr', paramValue: '[GDPR]' },
      { text: 'Coppa', paramName: 'coppa', paramValue: '[COPPA]' },
      { text: 'Consent', paramName: 'consent', paramValue: '[CONSENT]' },
      { text: 'CCPA', paramName: 'us_privacy', paramValue: '[US_PRIVACY]' },
      { text: 'IFA Type', paramName: 'ifa_type', paramValue: '[IFA_TYPE]' },
      { text: 'Limited Ad Tracking', paramName: 'is_lat', paramValue: '[LIMITED_AD_TRACKING]' },
    ],
    display: true,
  },
  {
    title: 'Stream',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'Genre ID', paramName: 'genre_id', paramValue: '[GENRE_ID]' },
      { text: 'Position', paramName: 'position', paramValue: '[POSITION]' },
      { text: 'Buyer UID', paramName: 'buyeruid', paramValue: '[BUYERUID]' },
      { text: 'SSAI Vendor', paramName: 'ssai_vendor', paramValue: '[SSAI_VENDOR]' },
      { text: 'Max Bitrate', paramName: 'max_bitrate', paramValue: '[MAXBITRATE]' },
      { text: 'Min Bitrate', paramName: 'min_bitrate', paramValue: '[MINBITRATE]' },
      { text: 'SSAI Enabled', paramName: 'ssai_enabled', paramValue: '[SSAI_ENABLED]' },
      { text: 'Rights Holder', paramName: 'rights_holder', paramValue: '[RIGHTS_HOLDER]' },
      { text: 'Player Height', paramName: 'player_height', paramValue: '[HEIGHT]' },
      { text: 'Player Width', paramName: 'player_width', paramValue: '[WIDTH]' },
    ],
    display: true,
  },
  {
    title: 'Custom',
    elementType: EQueryParamCategoryElementType.DROPDOWN,
    multiSelect: true,
    mandatory: false,
    queryParams: [
      { text: 'Custom 5', paramName: 'custom_5', paramValue: '[CUSTOM_5]' },
      { text: 'Custom 6', paramName: 'custom_6', paramValue: '[CUSTOM_6]' },
      { text: 'Custom 7', paramName: 'custom_7', paramValue: '[CUSTOM_7]' },
      { text: 'Custom 8', paramName: 'custom_8', paramValue: '[CUSTOM_8]' },
      { text: 'Custom 9', paramName: 'custom_9', paramValue: '[CUSTOM_9]' },
      { text: 'Custom 10', paramName: 'custom_10', paramValue: '[CUSTOM_10]' },
      { text: 'Custom 11', paramName: 'custom_11', paramValue: '[CUSTOM_11]' },
      { text: 'Custom 12', paramName: 'custom_12', paramValue: '[CUSTOM_12]' },
      { text: 'Custom 13', paramName: 'custom_13', paramValue: '[CUSTOM_13]' },
      { text: 'Custom 14', paramName: 'custom_14', paramValue: '[CUSTOM_14]' },
      { text: 'Custom 15', paramName: 'custom_15', paramValue: '[CUSTOM_15]' },
      { text: 'Custom 16', paramName: 'custom_16', paramValue: '[CUSTOM_16]' },
      { text: 'Custom 17', paramName: 'custom_17', paramValue: '[CUSTOM_17]' },
      { text: 'Custom 18', paramName: 'custom_18', paramValue: '[CUSTOM_18]' },
    ],
    display: true,
  },
];

export const NetworkQueryParamGroups: IQueryParamGroup[] = [
  {
    name: 'Define your ad break',
    queryParamCategories: AdBreakParamCategories,
  },
  {
    name: 'Pass viewer & contextual information to advertisers',
    queryParamCategories: AdditionalParamsCategories,
  },
];

// NETWORK PARAM DEPENDENCIES //

// key: title of display dependent param category
// value: array of paramNames for selected Params that should trigger displaying the dependent param category
// ANY ==> if any of these params are selected, display dependent param category
// ALL ==> if all of these params are selected, display dependent param category
export const displayDependencies: { [k: string]: string[] } = {
  App: [ALL, 'app_bundle'],
  Site: [ALL, 'site_page'],
  'Min & Max Ad Duration': [ANY, 'pod_duration', 'slot_count'],
};

// paramNames that have params which should automatically be selected when the key param is selected
// exclusion: suggests that if this param is selected as a dependent of another param, we should deselect any exclusions
export const selectDependencies: { [k: string]: IQueryParam[] } = {
  app_bundle: [
    { text: 'App Name', paramName: 'app_name', paramValue: '[APP_NAME]' },
    { text: 'App Domain', paramName: 'app_domain', paramValue: '[APP_DOMAIN]' },
    { text: 'App Version', paramName: 'app_version', paramValue: '[APP_VERSION]' },
    { text: 'App Category', paramName: 'app_category', paramValue: '[APP_CATEGORY]' },
    { text: 'App Store URL', paramName: 'app_store_url', paramValue: '[APP_STORE_URL]' },
  ],
  site_page: [{ text: 'Site Name', paramName: 'site_name', paramValue: '[SITE_NAME]' }],
  ad_breaks: [
    {
      text: 'Pod Duration',
      paramName: 'pod_duration',
      paramValue: '[POD_DURATION]',
      exclusions: ['slot_count'],
    },
    { text: 'Min Ad Duration', paramName: 'min_ad_duration', paramValue: '6' },
    { text: 'Max Ad Duration', paramName: 'max_ad_duration', paramValue: '120' },
  ],
  pod_duration: [
    { text: 'Min Ad Duration', paramName: 'min_ad_duration', paramValue: '6' },
    { text: 'Max Ad Duration', paramName: 'max_ad_duration', paramValue: '120' },
  ],
  slot_count: [
    { text: 'Min Ad Duration', paramName: 'min_ad_duration', paramValue: '6' },
    { text: 'Max Ad Duration', paramName: 'max_ad_duration', paramValue: '120' },
  ],
};

// params that should be selected if one or more params are selected
// i.e. min / max ad duration should be selected along with EITHER pod_duration or slot_count
export const multiSelectDependencies: { [k: string]: string[] } = {
  min_ad_duration: ['pod_duration', 'slot_count'],
  max_ad_duration: ['pod_duration', 'slot_count'],
};
